import React from 'react';
import {
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import PlayerCard from './PlayerCard';
import './OverflowQueue.css';

function OverflowQueue({ players, getPlayerMeritRank, getPlayerPaceRank, requestFulfillment }) {
  const {
    isOver,
    setNodeRef,
  } = useDroppable({
    id: 'overflow',
  });

  const unassignedCount = players.filter(p => p.isUnassigned).length;
  const benchedCount = players.length - unassignedCount;

  const queueClasses = [
    'overflow-queue',
    'scratch-pad',
    isOver ? 'drag-over' : '',
    players.length === 0 ? 'empty' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={queueClasses} ref={setNodeRef}>
      <div className="queue-header">
        <div className="header-title">
          <span className="header-icon">🪑</span>
          <h3>Bench / Scratch Pad</h3>
        </div>
        <span className="player-count">
          {players.length} player{players.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="queue-description">
        <p>Drag players here to temporarily remove from tee sheet. Drag back to add to groups.</p>
        <div className="bench-stats">
          {benchedCount > 0 && (
            <span className="stat-badge benched">
              <span className="badge-icon">🔄</span>
              {benchedCount} benched
            </span>
          )}
          {unassignedCount > 0 && (
            <span className="stat-badge unassigned">
              <span className="badge-icon">⚠️</span>
              {unassignedCount} unassigned
            </span>
          )}
        </div>
      </div>

      {/* Always-visible drop zone at top */}
      <DropZone />

      <div className="players-container">
        {players.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">✨</span>
            <p>Bench is empty</p>
            <small>All players are assigned to groups</small>
          </div>
        ) : (
          players.map((player) => (
            <DraggableOverflowPlayer
              key={player.ghin}
              player={player}
              meritRank={getPlayerMeritRank ? getPlayerMeritRank(player.ghin) : 'N/A'}
              paceRank={getPlayerPaceRank ? getPlayerPaceRank(player.ghin) : 'N/A'}
              requestFulfillment={requestFulfillment}
            />
          ))
        )}
      </div>
    </div>
  );
}

function DraggableOverflowPlayer({ player, meritRank, paceRank, requestFulfillment }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useDraggable({
    id: player.ghin,
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const cardClasses = [
    'draggable-overflow-player',
    player.isUnassigned ? 'unassigned-player' : 'benched-player'
  ].filter(Boolean).join(' ');

  // Look up per-player fulfillment data
  const partnerFulfillment = requestFulfillment?.partner?.[player.ghin] || null;
  const timeFulfillment = requestFulfillment?.time?.[player.ghin] || null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cardClasses}
    >
      <PlayerCard
        player={player}
        meritRank={meritRank}
        paceRank={paceRank}
        isInOverflow={true}
        isUnassigned={player.isUnassigned}
        isDragging={isDragging}
        partnerFulfillment={partnerFulfillment}
        timeFulfillment={timeFulfillment}
      />
      <div className={`bench-badge ${player.isUnassigned ? 'unassigned' : 'benched'}`}>
        <span className="badge-icon">{player.isUnassigned ? '⚠️' : '🪑'}</span>
        <span className="badge-text">{player.isUnassigned ? 'Unassigned' : 'Benched'}</span>
      </div>
    </div>
  );
}

function DropZone() {
  const {
    isOver,
    setNodeRef,
  } = useDroppable({
    id: 'overflow-drop-zone',
  });

  const dropZoneClasses = [
    'overflow-drop-zone',
    isOver ? 'drag-over' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={dropZoneClasses} ref={setNodeRef}>
      <span className="drop-placeholder">
        Drop players here to remove from groups
      </span>
    </div>
  );
}

export default OverflowQueue;